/**
 * Version: V0.0.1.1
 * Created: 2026-06-13
 * Description: Cookie consent banner matching the original template layout and behavior.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const TRANSLATIONS = {
  en: {
    message: "We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.",
    policyLink: "cookie policy",
    accept: "Accept"
  },
  fr: {
    message: "Nous utilisons des cookies pour améliorer votre expérience. En continuant à visiter ce site, vous acceptez notre utilisation des cookies.",
    policyLink: "politique en matière de cookies",
    accept: "Accepter"
  },
  ar: {
    message: "نحن نستخدم ملفات تعريف الارتباط لتحسين تجربتك. من خلال الاستمرار في زيارة هذا الموقع، فإنك توافق على استخدامنا لملفات تعريف الارتباط.",
    policyLink: "سياسة ملفات تعريف الارتباط",
    accept: "موافق"
  }
};

export default function CookieConsentBanner() {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);
  const isRtl = language === 'ar';

  useEffect(() => {
    // Check if cookie_accepted cookie exists
    const cookieAccepted = document.cookie.split(';').some((item) => item.trim().startsWith('cookies_accepted='));
    if (!cookieAccepted) {
      setVisible(true);
      // Auto hide after 10 seconds as in template version 1.4.0
      const timer = setTimeout(() => {
        setVisible(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    setVisible(false);
    const date = new Date();
    date.setTime(date.getTime() + (365 * 24 * 60 * 60 * 1000)); // 1 year
    document.cookie = "cookies_accepted=true; expires=" + date.toUTCString() + ";path=/;SameSite=Strict";
  };

  if (!visible) return null;

  const t = TRANSLATIONS[language] || TRANSLATIONS.en;

  return (
    <div id="cookie-banner" className="fixed bottom-0 left-0 w-full z-50" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="bg-slate-800 text-white shadow-lg p-4 md:p-6 w-full">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <span className={`text-sm text-slate-300 text-center ${isRtl ? 'md:text-right' : 'md:text-left'}`}>
            {t.message}{' '}
            <Link to="/cookie-policy" className="text-violet-400 underline hover:text-violet-300">
              {t.policyLink}
            </Link>
            .
          </span>
          <button
            onClick={acceptCookies}
            className="bg-violet-600 text-white font-semibold px-6 py-2 rounded-lg shadow-md hover:bg-violet-700 transition-colors whitespace-nowrap"
          >
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
